import React, { useState, useEffect, useImperativeHandle } from "react";
import { useDispatch, useSelector } from "react-redux";
import more from "../../assets/more.svg";
import collapse from "../../assets/collapse.svg";
import { useNavigate } from "react-router-dom";
import QuestionnaireCompletion from "../Modals/QuestionnaireCompletion";
import { updateLevel5, updateLevel7 } from "../../store/QuestionnaireSlice";
import trash from "../../assets/trash.svg";
import expand from "../../assets/expand.svg";
import editPen from "../../assets/edit_pen.svg";
import { useUser } from "../../providers/UserProvider";
import { useInterceptor } from "providers/InterceptorProvider";
import check from "../../assets/check.png";
import { forwardRef } from "react";
import { useRef } from "react";

const Level7 = forwardRef(
  ({ onNext, onBack, placement, editError, setEditError }, ref) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { user } = useUser();
    const { api } = useInterceptor();

    // Get all required data from Redux store
    const valueStreamActivities = useSelector(
      (state) => state.questionnaire.level7Data
    );
    const valueStreams = useSelector((state) => state.questionnaire.level5Data);
    const level1Data = useSelector((state) => state.questionnaire.level1Data);
    const level2Data = useSelector((state) => state.questionnaire.level2Data);
    const level3Data = useSelector((state) => state.questionnaire.level3Data);
    const level4Data = useSelector((state) => state.questionnaire.level4Data);
    const level6Data = useSelector((state) => state.questionnaire.level6Data);
    const activityInputRef = useRef({});

    const [activeDropdown, setActiveDropdown] = useState(null);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [newActivityName, setNewActivityName] = useState({});
    const [isAddingActivity, setIsAddingActivity] = useState({});
    const [showSubActivity, setShowSubActivity] = useState([]);
    const [editStreamindex, setEditStreamIndex] = useState(null);
    const [editStreamName, setEditStreamName] = useState("");
    const [editSubActivityIndex, setEditSubActivityIndex] = useState(null);
    const [editSubActivityName, setEditSubActivityName] = useState("");
    const [error, setError] = useState("");
    const [organization, setOrganization] = useState("");
    const questions = useSelector((state) => state.questionnaire.questions);

    useImperativeHandle(ref, () => ({
      handleSubmit,
    }));

    const handleAddActivityClick = (streamName) => {
      setIsAddingActivity({
        ...isAddingActivity,
        [streamName]: true,
      });
      setNewActivityName({
        ...newActivityName,
        [streamName]: "",
      });
      setTimeout(() => {
        if (activityInputRef.current[streamName]) {
          activityInputRef.current[streamName].focus();
        }
      }, 0);
    };

    const handleAddActivity = (streamName) => {
      if (!newActivityName[streamName]?.trim()) return;

      const activities = valueStreamActivities[streamName] || [];
      const updatedActivities = {
        ...valueStreamActivities,
        [streamName]: [
          ...activities,
          { _id: "", name: newActivityName[streamName] },
        ],
      };
      dispatch(updateLevel7(updatedActivities));

      // Reset the input state
      setIsAddingActivity({
        ...isAddingActivity,
        [streamName]: false,
      });
      setNewActivityName({
        ...newActivityName,
        [streamName]: "",
      });
    };

    const handleDeleteActivity = (streamName, index) => {
      const updatedActivities = {
        ...valueStreamActivities,
        [streamName]: valueStreamActivities[streamName].filter(
          (_, i) => i !== index
        ),
      };
      dispatch(updateLevel7(updatedActivities));
      setActiveDropdown(null);
    };

    const handleEditStream = (index, currName) => {
      setEditStreamIndex(index);
      setEditStreamName(currName);
      setActiveDropdown(null);
    };

    const handleSaveStream = (index) => {
      if (editStreamName.trim()) {
        const updatedStream = valueStreams.map((stream, orignalindex) =>
          orignalindex === index ? { ...stream, name: editStreamName } : stream
        );

        let updatedVSActivities = { ...valueStreamActivities };
        updatedVSActivities = {
          ...updatedVSActivities,
          [editStreamName]: updatedVSActivities[valueStreams[index].name],
        };
        if (editStreamName !== valueStreams[index].name) {
          delete updatedVSActivities[valueStreams[index].name];
        }
        // console.log("updatedActivities -> ", updatedVSActivities);
        dispatch(updateLevel5(updatedStream));
        dispatch(updateLevel7(updatedVSActivities));
        setEditStreamName("");
        setEditStreamIndex(null);
      } else {
        setError("Stream title cannot be empty");
        setEditStreamIndex(null);
        return "Stream title cannot be empty";
      }
    };

    const handleEditSubActivity = (streamName, index, currentName) => {
      setEditSubActivityIndex({ streamName, index });
      setEditSubActivityName(currentName);
    };

    const handleSaveSubActivity = () => {
      if (editSubActivityName.trim()) {
        const { streamName, index } = editSubActivityIndex;

        const updatedActivities = {
          ...valueStreamActivities,
          [streamName]: valueStreamActivities[streamName].map((activity, i) =>
            i === index ? { ...activity, name: editSubActivityName } : activity
          ),
        };

        dispatch(updateLevel7(updatedActivities));
        setEditSubActivityIndex(null);
        setEditSubActivityName("");
      } else {
        setError("Sub-Activity title cannot be empty");
        setEditSubActivityIndex(null);
        return "Sub-Activity title cannot be empty";
      }
    };
    const getOrganization = async () => {
      try {
        const res = await api.get(
          `/organization/getorganization/${user.organizationId}`
        );
        if (res.status === 200) {
          setOrganization(res.data.data);
        }
        // console.log("res -> ", res);
      } catch (err) {
        console.error(err);
      }
    };
    useEffect(() => {
      getOrganization();
    }, [user]);

    const handleApiCalls = async () => {
      try {
        console.log(user.organizationId);
        // First payload (Value-Stream)
        const valueStreamPayload = {
          type: "Value-Stream View",
          name: organization?.name || "Aceable Inc. Co.",
          organizationId: user.organizationId,
          children: level1Data.map((segment) => ({
            name: segment.name,
            children: valueStreams
              .filter((stream) => stream.product === segment.name)
              .map((stream) => ({
                name: stream.name,
                children:
                  valueStreamActivities[stream.name]?.map((activity) => ({
                    name: activity.name,
                  })) || [],
              })),
          })),
        };

        // Second payload (Activity-View)
        const activityViewPayload = {
          type: "Activity View",
          name: organization?.name || "Aceable Inc. Co.",
          organizationId: user.organizationId,
          children: level2Data.map((activity) => ({
            name: activity.name,
            children: level4Data.map((subActivity) => ({
              name: subActivity.name,
            })),
          })),
        };

        // Third payload (Functional-View)
        const functionalViewPayload = {
          type: "Functional View",
          name: organization?.name || "Aceable Inc. Co.",
          organizationId: user.organizationId,
          children: level3Data.map((functionalUnit) => ({
            name: functionalUnit.name,
            children:
              level6Data[functionalUnit.name]?.map((department) => ({
                name: department.name,
              })) || [],
          })),
        };

        // Make all three API calls simultaneously
        const [
          valueStreamResponse,
          activityViewResponse,
          functionalViewResponse,
        ] = await Promise.all([
          api.post("/generate-tree", valueStreamPayload),
          api.post("/generate-tree", activityViewPayload),
          api.post("/generate-tree", functionalViewPayload),
        ]);

        if (
          valueStreamResponse.data.success &&
          activityViewResponse.data.success &&
          functionalViewResponse.data.success
        ) {
          console.log("All three trees generated successfully");
          navigate("/organization-tree");
        }
      } catch (error) {
        console.error("Error generating trees:", error);
      }
    };

    const handleSubmit = () => {
      const updatedActivities = { ...valueStreamActivities };
      for (const valueStream in newActivityName) {
        if (newActivityName[valueStream]) {
          const activities = valueStreamActivities[valueStream] || [];
          updatedActivities[valueStream] = [
            ...activities,
            { _id: "", name: newActivityName[valueStream] },
          ];
        }
      }
      setNewActivityName({});
      dispatch(updateLevel7(updatedActivities));
      let hasIncompleteFields = false;

      for (const stream of valueStreams) {
        if (
          (valueStreamActivities[stream.name] &&
            valueStreamActivities[stream.name].length > 0) ||
          newActivityName[stream.name]
        ) {
          continue;
        } else {
          hasIncompleteFields = true;
          break;
        }
      }
      if (valueStreams.length === 0) {
        setError(
          "Please add at least one value stream at level 5 before submission"
        );
        return "Please add at least one value stream at level 5 before submission";
      }
      if (hasIncompleteFields) {
        setError("Please add at least one sub activity before submission");
        return "Please add at least one sub activity before submission";
      }

      setError(""); // Clear the error if all fields are complete

      if (placement !== "edit") {
        setShowSuccessPopup(true);
      }
    };

    const handlePopupClose = () => {
      setShowSuccessPopup(false);
      handleApiCalls();
    };
    const handleDeleteStream = (index) => {
      // Create a new array without the deleted stream using the index
      const updatedStreams = valueStreams.filter(
        (_, streamIndex) => streamIndex !== index
      );

      // Update the Redux store with the new array of streams
      dispatch(updateLevel5(updatedStreams));

      // Close the dropdown after deleting the stream
      setActiveDropdown(null);
    };

    useEffect(() => {
      const handleClickOutside = () => setActiveDropdown(null);
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    return (
      <div>
        <div
          className={` pb-6 px-10 mx-auto rounded-lg max-w-[1200px] ${
            placement !== "edit" ? "bg-[#F4F5F6] pt-[30px]" : "bg-white"
          }`}
        >
          <div className="bg-[#1C3F3A] text-white px-[10px] py-[6px] w-fit rounded-md text-[12px]">
            Level 7
          </div>
          <p
            className={`mt-3 mb-6 font-medium ${
              placement === "edit" ? "text-[1.125rem]" : "text-[1.25rem]"
            }`}
          >
            {questions[6]?.description}
          </p>
          {(error || editError) && (
            <p className="flex justify-between items-center bg-red-100 border-l-2 border-0 mb-5 border-red-400 text-red-700 px-4 py-3">
              {error ? error : editError}
              <span
                className="cursor-pointer"
                onClick={() => {
                  if (editError) {
                    setEditError("");
                  } else {
                    setError("");
                  }
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 26 26"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </span>
            </p>
          )}
          <div className="space-y-3">
            {valueStreams.map((stream, streamIndex) => (
              <div
                key={streamIndex}
                className={`${
                  showSubActivity.includes(streamIndex) ? "" : "space-y-5"
                }`}
              >
                <div className="flex gap-3 items-center">
                  <img
                    src={
                      !showSubActivity.includes(streamIndex) ? collapse : expand
                    }
                    alt=""
                    onClick={() =>
                      setShowSubActivity(
                        showSubActivity.includes(streamIndex)
                          ? showSubActivity.filter(
                              (index) => index !== streamIndex
                            )
                          : [...showSubActivity, streamIndex]
                      )
                    }
                  />
                  {editStreamindex === streamIndex ? (
                    <div className="max-w-[210px]">
                      <input
                        type="text"
                        value={editStreamName}
                        onChange={(e) => setEditStreamName(e.target.value)}
                        onBlur={() => handleSaveStream(streamIndex)}
                        onKeyPress={(e) => {
                          if (e.key === "Enter") {
                            handleSaveStream(streamIndex);
                          }
                        }}
                        className="border border-gray-300 rounded-lg px-[0.75rem] py-[0.325rem] text-[15px] w-full outline-none"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div
                      className="flex items-center gap-3 min-w-[214px] justify-between px-[0.75rem] py-[0.325rem] bg-white rounded-lg shadow-sm border border-[#D9D9D9]"
                      onClick={() => handleEditStream(streamIndex, stream.name)}
                    >
                      <span className="font-medium">{stream.name}</span>
                      <div className="relative">
                        <button
                          className="flex justify-center"
                          onClick={(e) => {
                            e.stopPropagation();
                            // setActiveDropdown(
                            //   activeDropdown === streamIndex
                            //     ? null
                            //     : streamIndex
                            // );
                            handleDeleteStream(streamIndex);
                          }}
                        >
                          <img src={trash} alt="" />
                        </button>
                        {/* {activeDropdown === streamIndex && (
                          <div className="absolute top-5 right-0 mt-2 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                            <button
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm border-b border-[#D9D9D9] flex items-center gap-2"
                              onClick={() =>
                                handleEditStream(streamIndex, stream.name)
                              }
                            >
                              <img src={editPen} alt="" />
                              Edit
                            </button>
                            <button
                              className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center gap-2"
                              onClick={() => handleDeleteStream(streamIndex)}
                            >
                              <img src={trash} alt="" />
                              Delete
                            </button>
                          </div>
                        )} */}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-3 relative pl-8">
                  {!showSubActivity.includes(streamIndex) && (
                    <div className="">
                      <div className="border-l-[1.5px] border-b-[1.5px] border-black w-[13.5px] h-[40.5px] absolute left-[9px] -top-[23px]"></div>
                      <div
                        className={`flex flex-wrap gap-3 ${
                          showSubActivity.includes(streamIndex) ? "mb-6" : ""
                        }`}
                      >
                        {valueStreamActivities[stream.name]?.map(
                          (activity, actIndex) =>
                            editSubActivityIndex?.index === actIndex &&
                            editSubActivityIndex?.streamName === stream.name ? (
                              <div className="max-w-[214px]">
                                <input
                                  type="text"
                                  value={editSubActivityName}
                                  onChange={(e) =>
                                    setEditSubActivityName(e.target.value)
                                  }
                                  onBlur={handleSaveSubActivity}
                                  onKeyPress={(e) => {
                                    if (e.key === "Enter") {
                                      handleSaveSubActivity();
                                    }
                                  }}
                                  className="border border-gray-300 rounded-lg px-[0.75rem] py-[0.325rem] text-[15px] w-full outline-none"
                                  autoFocus
                                />
                              </div>
                            ) : (
                              <div
                                key={actIndex}
                                className={`flex items-center min-w-[214px] gap-3 justify-between px-[0.75rem] py-[0.325rem] bg-white rounded-lg shadow-sm border border-[#D9D9D9]`}
                                onClick={() =>
                                  handleEditSubActivity(
                                    stream.name,
                                    actIndex,
                                    activity.name
                                  )
                                }
                              >
                                <span className="font-medium w-[98%]">
                                  {activity.name}
                                </span>
                                <div className="relative flex">
                                  <button
                                    className="flex justify-center"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      // setActiveDropdown(
                                      //   `${stream.name}-${actIndex}`
                                      // );
                                      handleDeleteActivity(
                                        stream.name,
                                        actIndex
                                      );
                                    }}
                                  >
                                    <img src={trash} alt="" />
                                  </button>
                                  {/* {activeDropdown ===
                                    `${stream.name}-${actIndex}` && (
                                    <div className="absolute top-5 right-0 mt-2 w-32 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                                      <button
                                        className="w-full px-4 py-2 text-left text-sm border-b border-[#D9D9D9] hover:bg-gray-100 flex items-center gap-2"
                                        onClick={() =>
                                          handleEditSubActivity(
                                            stream.name,
                                            actIndex,
                                            activity.name
                                          )
                                        }
                                      >
                                        <img src={editPen} alt="" />
                                        Edit
                                      </button>
                                      <button
                                        className="w-full px-4 py-2 text-left hover:bg-gray-100 text-sm flex items-center gap-2"
                                        onClick={() =>
                                          handleDeleteActivity(
                                            stream.name,
                                            actIndex
                                          )
                                        }
                                      >
                                        <img src={trash} alt="" />
                                        Delete
                                      </button>
                                    </div>
                                  )} */}
                                </div>
                              </div>
                            )
                        )}
                        {/* {isAddingActivity[stream.name] ? ( */}
                        <div className="flex gap-2 w-[214px]">
                          <div className="relative w-[210px]">
                            <input
                              type="text"
                              value={newActivityName[stream.name] || ""}
                              onChange={(e) => {
                                console.log(newActivityName);
                                setNewActivityName({
                                  ...newActivityName,
                                  [stream.name]: e.target.value,
                                });
                              }}
                              placeholder="Sub activity Title"
                              className="px-4 py-[0.325rem] w-full border border-[#D9D9D9] rounded-[6px] focus:outline-none placeholder-[#1C3F3A] placeholder-opacity-50"
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  handleAddActivity(stream.name);
                                }
                              }}
                              ref={(el) =>
                                (activityInputRef.current[stream.name] = el)
                              }
                            />
                            <img
                              src={check}
                              alt=""
                              className="absolute right-2 top-3 max-sm:hidden"
                            />
                          </div>
                          {/* <input
                             type="text"
                             value={newActivityName[stream.name] || ""}
                             onChange={(e) =>
                               setNewActivityName({
                                 ...newActivityName,
                                 [stream.name]: e.target.value,
                               })
                             }
                             placeholder="Enter Sub-Activity Name"
                             className="flex-1 px-3 py-2 h-fit border border-[#1C3F3A] rounded-md focus:outline-none focus:ring-1 focus:ring-[#1C3F3A]"
                             onKeyPress={(e) => {
                               if (e.key === "Enter") {
                                 handleAddActivity(stream.name);
                               }
                             }}
                           />
                           <button
                             onClick={() => handleAddActivity(stream.name)}
                             className="px-4 py-2 h-fit bg-[#55C880] text-white rounded-md hover:bg-[#152e2b] transition-colors"
                           >
                             Add
                           </button> */}
                        </div>
                        {/* ) : ( */}
                        <button
                          onClick={() => {
                            handleAddActivityClick(stream.name);
                            handleAddActivity(stream.name);
                          }}
                          className="relative py-[0.375rem] w-[214px] h-fit font-medium rounded-[6px] text-[#1C3F3A] hover:bg-[#f0f0f0] transition-colors"
                        >
                          <svg
                            width="100%"
                            height="100%"
                            viewBox="0 0 210 36"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="absolute inset-0"
                            preserveAspectRatio="none"
                          >
                            <rect
                              x="0.5"
                              y="0.5"
                              width="99%"
                              height="97%"
                              rx="5.5"
                              fill="white"
                              stroke="#1C3F3A"
                              strokeWidth="1"
                              strokeDasharray="4 4"
                            />
                          </svg>
                          <span className="relative z-10">
                            Add New Sub-Activity
                          </span>
                        </button>
                        {/* )} */}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
        {placement !== "edit" && (
          <div className="flex justify-between mt-[26px]">
            <button
              className="border border-[#D9D9D9] text-[#1C3F3A] flex gap-3 justify-center items-center py-[0.825rem] rounded-[10px] font-inter font-medium px-5 hover:bg-[#f0f0f0] transition-colors"
              onClick={onBack}
            >
              <svg
                width="17"
                height="8"
                viewBox="0 0 17 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.80775 0.192239L4.52125 0.899989L1.92125 3.49999H17V4.49999H1.92125L4.5155 7.09999L3.802 7.80774L0 3.99999L3.80775 0.192239Z"
                  fill="#1C3F3A"
                />
              </svg>
              Back
            </button>
            <button
              className="bg-[#55C880] flex items-center justify-center gap-3 text-white pl-5 pr-6 py-[0.825rem] rounded-[10px] overflow-hidden"
              onClick={handleSubmit}
            >
              Complete & Submit
              <svg
                width="17"
                height="8"
                viewBox="0 0 17 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13.1923 7.80776L12.4788 7.10001L15.0788 4.50001H0V3.50001H15.0788L12.4845 0.900011L13.198 0.192261L17 4.00001L13.1923 7.80776Z"
                  fill="white"
                />
              </svg>
            </button>
          </div>
        )}

        {showSuccessPopup && (
          <QuestionnaireCompletion onClose={handlePopupClose} />
        )}
      </div>
    );
  }
);

export default Level7;
